import { getDistanceKm, type GeoCoordinateOption } from '@/lib/geo'
import { hasUniversityCoordinates } from '@/lib/locations/universityDataset'
import type { LngLat } from '@/lib/mock/mapLocations'
import type { LocationUniversityTravelStat } from '@/lib/mock/locationPages'

/**
 * Estimates road distance from great-circle distance.
 * This is not a routed path; a routing provider can replace
 * `computeTravelStatsFromCoordinates` later without changing university JSON.
 */
const ROAD_DISTANCE_FACTOR = 1.22

const WALK_SPEED_KMH = 4.35
const BIKE_SPEED_KMH = 14
const CAR_SPEED_KMH = 21
const PUBLIC_TRANSPORT_SPEED_KMH = 11.5
const PUBLIC_TRANSPORT_BASE_MINUTES = 8

export const TRAVEL_STAT_LABELS = {
  distance: 'Distance',
  publicTransport: 'Public transport',
  byCar: 'By car',
  byBike: 'By bike',
  onFoot: 'On foot',
} as const

const MOBILE_TRAVEL_STAT_LABELS: Partial<Record<string, string>> = {
  [TRAVEL_STAT_LABELS.publicTransport]: 'Transit',
  [TRAVEL_STAT_LABELS.byCar]: 'Car',
  [TRAVEL_STAT_LABELS.byBike]: 'Bike',
  [TRAVEL_STAT_LABELS.onFoot]: 'Walk',
}

export function getMobileTravelStatLabel(label: string): string {
  return MOBILE_TRAVEL_STAT_LABELS[label] ?? label
}

export function formatTravelDistanceKm(distanceKm: number): string {
  if (distanceKm < 0.05) {
    return '0 km'
  }

  if (distanceKm >= 10) {
    return `${Math.round(distanceKm)} km`
  }

  const rounded = Math.round(distanceKm * 10) / 10
  const wholeNumber = Math.round(rounded)

  if (Math.abs(rounded - wholeNumber) < 0.05) {
    return `${wholeNumber} km`
  }

  return `${String(rounded).replace('.', ',')} km`
}

export function parseTravelDistanceKm(value: string): number {
  return Number.parseFloat(value.replace(' km', '').replace(',', '.'))
}

function toLngLat(coordinate: GeoCoordinateOption): LngLat {
  return [coordinate.longitude, coordinate.latitude]
}

function estimateTravelMinutes(distanceKm: number, speedKmh: number, baseMinutes = 0): number {
  if (distanceKm < 0.05) {
    return 0
  }

  const minutes = Math.round((distanceKm / speedKmh) * 60 + baseMinutes)

  if (baseMinutes > 0) {
    return Math.max(baseMinutes, minutes)
  }

  return Math.max(1, minutes)
}

/** Coordinate-based travel estimates for university/property pairs. */
export function computeTravelStatsFromCoordinates(
  origin: GeoCoordinateOption,
  destination: GeoCoordinateOption,
): LocationUniversityTravelStat[] {
  const straightLineKm = getDistanceKm(toLngLat(origin), toLngLat(destination))
  const roadDistanceKm = straightLineKm * ROAD_DISTANCE_FACTOR

  return [
    { value: formatTravelDistanceKm(roadDistanceKm), label: TRAVEL_STAT_LABELS.distance },
    {
      value: `${estimateTravelMinutes(roadDistanceKm, PUBLIC_TRANSPORT_SPEED_KMH, PUBLIC_TRANSPORT_BASE_MINUTES)} min`,
      label: TRAVEL_STAT_LABELS.publicTransport,
    },
    {
      value: `${estimateTravelMinutes(roadDistanceKm, CAR_SPEED_KMH)} min`,
      label: TRAVEL_STAT_LABELS.byCar,
    },
    {
      value: `${estimateTravelMinutes(roadDistanceKm, BIKE_SPEED_KMH)} min`,
      label: TRAVEL_STAT_LABELS.byBike,
    },
    {
      value: `${estimateTravelMinutes(roadDistanceKm, WALK_SPEED_KMH)} min`,
      label: TRAVEL_STAT_LABELS.onFoot,
    },
  ]
}

/** Estimates travel stats from coordinates. Does not persist derived times. */
export function resolveUniversityTravelStats(params: {
  origin?: GeoCoordinateOption | null
  destination?: { latitude: number | null; longitude: number | null } | null
}): LocationUniversityTravelStat[] | undefined {
  if (params.origin && params.destination && hasUniversityCoordinates(params.destination)) {
    return computeTravelStatsFromCoordinates(params.origin, params.destination)
  }

  return undefined
}
